import type { EraIndex } from '../types';

/**
 * XP granted when an era's boss fight greenlights. Escalates per era so later,
 * harder milestones feel weightier. Tunable. See
 * docs/superpowers/specs/2026-06-17-xp-leveling-system-design.md.
 */
export const ERA_CLEAR_XP: Record<EraIndex, number> = {
  0: 1500,
  1: 2000,
  2: 2500,
  3: 3000,
  4: 3500,
  5: 4000,
};

/**
 * Cumulative XP required to REACH a level. cumXpForLevel(1) === 0.
 * Quadratic curve 250·L·(L-1): L2=500, L5=5000, L8=14000, L11=27500.
 * The per-level span (L→L+1) is therefore 500·L — a gently rising cost.
 */
export function cumXpForLevel(level: number): number {
  return 250 * level * (level - 1);
}

/**
 * Level reached for a given total XP — the inverse of cumXpForLevel.
 * Solving 250·L·(L-1) <= xp gives L = (1 + sqrt(1 + xp/62.5)) / 2.
 * The thresholds land on perfect squares (1 + cumXp/62.5 === (2L-1)²),
 * so this is exact at every boundary. Floored, min 1.
 */
export function levelForXp(totalXp: number): number {
  if (totalXp <= 0) return 1;
  return Math.floor((1 + Math.sqrt(1 + totalXp / 62.5)) / 2);
}

export interface XpBreakdown {
  /** Current level (>= 1). */
  level: number;
  /** XP earned since the current level's threshold. */
  xpIntoLevel: number;
  /** XP span of the current level (next threshold − current threshold). */
  xpForNext: number;
  /** xpIntoLevel / xpForNext, clamped to [0,1]. */
  pct: number;
}

/** Decompose a total XP into the fields the progress bar needs. */
export function xpBreakdown(totalXp: number): XpBreakdown {
  const safe = Math.max(0, totalXp);
  const level = levelForXp(safe);
  const base = cumXpForLevel(level);
  const next = cumXpForLevel(level + 1);
  const span = next - base;
  const into = safe - base;
  const pct = span > 0 ? Math.min(1, Math.max(0, into / span)) : 0;
  return { level, xpIntoLevel: into, xpForNext: span, pct };
}

/** Cosmetic Fallout-flavored rank for a level band. Pure lookup, never gates anything. */
const RANK_BANDS: ReadonlyArray<{ min: number; title: string }> = [
  { min: 1, title: 'Vault Newcomer' },
  { min: 3, title: 'Wasteland Scout' },
  { min: 5, title: 'Settlement Builder' },
  { min: 7, title: 'Wasteland Operator' },
  { min: 9, title: 'Bunker Veteran' },
  { min: 11, title: 'Overseer-Elect' },
];

export function rankForLevel(level: number): string {
  let title = RANK_BANDS[0].title;
  for (const band of RANK_BANDS) {
    if (level >= band.min) title = band.title;
  }
  return title;
}
